import { Box, ButtonBase, Paper, SwipeableDrawer, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { Project } from "../../../api/Projects";
import { Task } from "../../../api/Tasks";
import { useProjectFeed, useTaskFeed } from "../../../misc/Feed";
import { findColor, neutralColor, projectColorText } from "../../../misc/projectColors";
import { tabBarOffset } from "../../../misc/MobileTabBar";
import { useWorkspace } from "../../../misc/Workspace";
import { ProjectCode } from "../../project/ProjectCode";
import { groupUnscheduled } from "../UnscheduledPanel";
import { useCompleteTask } from "../useCompleteTask";
import { DayTaskRow } from "./DayTaskRow";
import { MobileTaskEditor } from "./MobileTaskEditor";
import { MobileSheet } from "./MobileSheet";

// projectBarHeight is the footer bar that shows while the drawer is closed, in px
export const projectBarHeight = 48;

// swipeOpen is how far up a swipe on the bar goes before it opens the drawer, in px
const swipeOpen = 20;

type Open = { kind: "project"; project: Project } | { kind: "unscheduled" };

// ProjectDrawer sits at the bottom of a phone's day and week pages: what each project
// has due in the range on screen, and the tasks with no due date.
export function ProjectDrawer(props: {
    // the range the due counts are read against, and the reader's own date
    dueFrom: string;
    dueTo: string;
    today: string;
}) {
    const ws = useWorkspace();
    const team = ws.team || undefined;

    const [drawer, setDrawer] = useState(false);
    const swipeFrom = useRef(0);
    const [open, setOpen] = useState<Open | null>(null);
    const [editing, setEditing] = useState<Task | null>(null);

    const projects = useProjectFeed({
        archived: false, dueFrom: props.dueFrom, dueTo: props.dueTo, today: props.today, team: team,
    });
    const unscheduled = useTaskFeed({ unscheduled: true, completed: false, team: team });

    const neutral = neutralColor();

    return (
        <>
            {/* holds the page's last row clear of the bar */}
            <Box sx={{ height: projectBarHeight }} />

            {/* the bar is its own footer rather than part of the drawer, so a tap on it
                isn't also read by the drawer as the start of a swipe */}
            <ButtonBase
                onClick={() => setDrawer(true)}
                onTouchStart={e => {
                    swipeFrom.current = e.touches[0].clientY;
                }}
                onTouchEnd={e => {
                    const moved = swipeFrom.current - e.changedTouches[0].clientY;
                    if (moved > swipeOpen) setDrawer(true);
                }}
                sx={{
                    position: "fixed",
                    bottom: tabBarOffset,
                    left: 0,
                    right: 0,
                    zIndex: theme => theme.zIndex.appBar,
                    height: projectBarHeight,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 0.5,
                    bgcolor: "background.paper",
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                    boxShadow: "0 -2px 6px rgba(0, 0, 0, 0.1)",
                }}
            >
                <Box sx={{ width: 32, height: 4, borderRadius: 2, bgcolor: "grey.300" }} />
                <Typography variant="body2" color="text.secondary">
                    Projects · {unscheduled.tasks.length} unscheduled
                </Typography>
            </ButtonBase>

            {/* once open, the drawer closes with a swipe down or a tap outside it */}
            <SwipeableDrawer
                anchor="bottom"
                open={drawer}
                onOpen={() => setDrawer(true)}
                onClose={() => setDrawer(false)}
                disableSwipeToOpen
                slotProps={{
                    paper: {
                        sx: { height: "70%", borderTopLeftRadius: 12, borderTopRightRadius: 12 },
                    },
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
                    <Box sx={{ width: 32, height: 4, borderRadius: 2, bgcolor: "grey.300" }} />
                </Box>

                <Box sx={{ height: "100%", overflow: "auto", p: 1.5, pb: "calc(12px + env(safe-area-inset-bottom))" }}>
                    {projects.LoadingOrErrorElement}
                    {unscheduled.LoadingOrErrorElement}

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                        {projects.projects.map(project => {
                            const color = findColor(project.color);

                            return (
                                <ButtonBase key={project.id} onClick={() => setOpen({ kind: "project", project: project })}
                                            sx={tileSx(color.bg)}>
                                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, width: "100%", minWidth: 0 }}>
                                        <ProjectCode code={project.code} color={project.color} />
                                        <Typography variant="body2" noWrap sx={{ fontWeight: 500, flex: 1, minWidth: 0 }}>
                                            {project.name}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                        {project.dueInRange} due · {project.openCount} open
                                    </Typography>
                                </ButtonBase>
                            );
                        })}

                        <ButtonBase onClick={() => setOpen({ kind: "unscheduled" })} sx={tileSx(neutral.bg)}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Unscheduled</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.75 }}>{unscheduled.tasks.length} open</Typography>
                        </ButtonBase>
                    </Box>
                </Box>
            </SwipeableDrawer>

            {open?.kind === "project" && (
                <ProjectTasksSheet project={open.project} onClose={() => setOpen(null)} onOpen={setEditing} />
            )}

            {open?.kind === "unscheduled" && (
                <MobileSheet title={<Typography variant="subtitle1">Unscheduled</Typography>}
                             onClose={() => setOpen(null)}>
                    <GroupedTasks
                        groups={groupUnscheduled(unscheduled.tasks).flatMap(group => group.goals.map(goal => ({
                            key: (group.project || 0) + "-" + (goal.goal || 0),
                            title: goal.name ? group.name + " › " + goal.name : group.name,
                            tasks: goal.tasks,
                        })))}
                        onOpen={setEditing}
                    />
                </MobileSheet>
            )}

            {editing && (
                <MobileTaskEditor key={editing.id} value={editing} onClose={() => setEditing(null)} onSaved={() => {}} />
            )}
        </>
    );
}

function tileSx(bg: string) {
    return {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.25,
        minWidth: 0,
        px: 1,
        py: 1,
        borderRadius: 1,
        bgcolor: bg,
        color: projectColorText(),
        textAlign: "left",
    } as const;
}

// ProjectTasksSheet lists a project's open tasks, goal by goal
function ProjectTasksSheet(props: { project: Project; onClose: () => void; onOpen: (task: Task) => void }) {
    const list = useTaskFeed({ project: props.project.id, completed: false });

    const byGoal = new Map<number, Task[]>();
    const noGoal: Task[] = [];
    for (const task of list.tasks) {
        if (task.goal) byGoal.set(task.goal, (byGoal.get(task.goal) || []).concat(task));
        else noGoal.push(task);
    }

    const groups = Array.from(byGoal.entries())
        .map(([goal, tasks]) => ({ key: String(goal), title: tasks[0].goalName, tasks: tasks }))
        .sort((a, b) => a.title.localeCompare(b.title));

    if (noGoal.length > 0) groups.push({ key: "none", title: "No goal", tasks: noGoal });

    return (
        <MobileSheet
            onClose={props.onClose}
            title={<>
                <ProjectCode code={props.project.code} color={props.project.color} />
                <Typography variant="subtitle1" noWrap>{props.project.name}</Typography>
            </>}
        >
            <Box sx={{ px: 1 }}>{list.LoadingOrErrorElement}</Box>
            <GroupedTasks groups={groups} onOpen={props.onOpen} />
            {!list.loading && list.tasks.length === 0 && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 4 }}>
                    Nothing open
                </Typography>
            )}
        </MobileSheet>
    );
}

function GroupedTasks(props: {
    groups: { key: string; title: string; tasks: Task[] }[];
    onOpen: (task: Task) => void;
}) {
    const complete = useCompleteTask();

    return (
        <>
            {props.groups.map(group => (
                <Box key={group.key}>
                    <Typography variant="caption" component="div" sx={{
                        px: 1.5, pt: 1, pb: 0.5,
                        color: "text.secondary", fontWeight: 600,
                    }}>
                        {group.title}
                    </Typography>
                    <Paper variant="outlined" square sx={{ borderLeft: 0, borderRight: 0 }}>
                        {group.tasks.map(task => (
                            <DayTaskRow
                                key={task.id}
                                task={task}
                                showDue
                                disabled={complete.loading}
                                onClick={() => props.onOpen(task)}
                                onToggle={checked => complete.callback({ id: task.id, completed: checked })}
                            />
                        ))}
                    </Paper>
                </Box>
            ))}
        </>
    );
}
